/**
 * Monday.com API Service
 * Handles all interactions with the Monday.com API
 */
class MondayAPIService {
  constructor(apiKey) {
    this.apiKey = apiKey;
    this.baseUrl = 'https://api.monday.com/v2';
    this.headers = {
      'Authorization': apiKey,
      'Content-Type': 'application/json',
      'API-Version': '2024-10'
    };
  }

  /**
   * Make a GraphQL query to Monday.com API
   */
  async makeQuery(query, variables = {}) {
    try {
      const response = await fetch(this.baseUrl, {
        method: 'POST',
        headers: this.headers,
        body: JSON.stringify({
          query,
          variables
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data.errors && data.errors.length > 0) {
        console.error('❌ GraphQL errors:', data.errors);
        throw new Error(data.errors[0].message);
      }

      return data.data;
    } catch (error) {
      console.error('Monday API Error:', error);
      throw error;
    }
  }

  /**
   * Test the API key by making a simple query
   */
  async testConnection() {
    const query = `
      query {
        me {
          id
          name
          email
        }
      }
    `;

    try {
      const result = await this.makeQuery(query);
      return result.me;
    } catch (error) {
      throw new Error('Invalid API key or connection failed');
    }
  }

  /**
   * Get all boards accessible with the current API key
   * Using the same structure as the working MondayCalendarTest implementation
   */
  async getBoards() {
    const query = `
      query {
        boards (limit: 200) {
          id
          name
          description
          board_kind
          columns {
            id
            title
            type
          }
        }
      }
    `;

    try {
      const result = await this.makeQuery(query);
      
      if (result.boards) {
        console.log(`✅ Fetched ${result.boards.length} boards`);
        return result.boards;
      } else {
        return [];
      }
    } catch (error) {
      console.error('❌ Error fetching boards:', error);
      throw new Error('Failed to fetch boards. Please check your API key permissions.');
    }
  }

  /**
   * Get details of a specific board
   */
  async getBoardDetails(boardId) {
    const query = `
      query ($boardId: [Int!]!) {
        boards(ids: $boardId) {
          id
          name
          description
          columns {
            id
            title
            type
            settings_str
          }
          groups {
            id
            title
            color
          }
        }
      }
    `;

    try {
      const result = await this.makeQuery(query, { boardId: [parseInt(boardId)] });
      return result.boards[0];
    } catch (error) {
      console.error('Error fetching board details:', error);
      throw new Error('Failed to fetch board details');
    }
  }

  /**
   * Create a new item (lead/contact) in the specified board
   */
  async createItem(boardId, itemName, columnValues = {}) {
    const query = `
      mutation ($boardId: Int!, $itemName: String!, $columnValues: JSON!) {
        create_item(
          board_id: $boardId,
          item_name: $itemName,
          column_values: $columnValues
        ) {
          id
          name
          created_at
        }
      }
    `;

    try {
      const result = await this.makeQuery(query, {
        boardId: parseInt(boardId),
        itemName,
        columnValues: JSON.stringify(columnValues)
      });

      return result.create_item;
    } catch (error) {
      console.error('Error creating item:', error);
      throw new Error('Failed to create item in Monday.com');
    }
  }

  /**
   * Create a lead item with common fields
   */
  async createLead(boardId, leadData) {
    const {
      name,
      email,
      phone,
      company,
      source = 'iMaven AI Chat',
      status = 'New Lead',
      notes = ''
    } = leadData;

    // Get board details to understand the column structure
    try {
      const boardDetails = await this.getBoardDetails(boardId);
      const columnValues = {};

      // Map common fields to Monday.com columns based on column types
      boardDetails.columns.forEach(column => {
        const columnTitle = column.title.toLowerCase();
        const columnType = column.type;

        switch (columnType) {
          case 'email':
            if (email && (columnTitle.includes('email') || columnTitle.includes('mail'))) {
              columnValues[column.id] = { email, text: email };
            }
            break;
          case 'phone':
            if (phone && (columnTitle.includes('phone') || columnTitle.includes('tel'))) {
              columnValues[column.id] = { phone, countryShortName: 'US' };
            }
            break;
          case 'text':
            if (company && (columnTitle.includes('company') || columnTitle.includes('organization'))) {
              columnValues[column.id] = company;
            } else if (source && columnTitle.includes('source')) {
              columnValues[column.id] = source;
            } else if (notes && (columnTitle.includes('note') || columnTitle.includes('comment'))) {
              columnValues[column.id] = notes;
            }
            break;
          case 'status':
            if (status && (columnTitle.includes('status') || columnTitle.includes('stage'))) {
              columnValues[column.id] = { label: status };
            }
            break;
          case 'long_text':
            if (notes && (columnTitle.includes('note') || columnTitle.includes('description'))) {
              columnValues[column.id] = notes;
            }
            break;
          default:
            // For unknown column types, we don't map any data
            break;
        }
      });

      return await this.createItem(boardId, name, columnValues);
    } catch (error) {
      console.error('Error creating lead:', error);
      throw new Error('Failed to create lead in Monday.com');
    }
  }

  /**
   * Update an existing item
   */
  async updateItem(itemId, columnValues) {
    const query = `
      mutation ($itemId: Int!, $columnValues: JSON!) {
        change_multiple_column_values(
          item_id: $itemId,
          column_values: $columnValues
        ) {
          id
          name
        }
      }
    `;

    try {
      const result = await this.makeQuery(query, {
        itemId: parseInt(itemId),
        columnValues: JSON.stringify(columnValues)
      });

      return result.change_multiple_column_values;
    } catch (error) {
      console.error('Error updating item:', error);
      throw new Error('Failed to update item in Monday.com');
    }
  }

  /**
   * Get board preview data (structure + sample items)
   * Using the exact same structure as the working MondayCalendarTest implementation
   */
  async getBoardPreview(boardId, itemLimit = 10) {
    const query = `
      query {
        boards (ids: [${boardId}]) {
          id
          name
          description
          columns {
            id
            title
            type
          }
          items_page(limit: ${itemLimit}) {
            items {
              id
              name
              created_at
              updated_at
              column_values {
                id
                text
                value
                type
              }
            }
          }
        }
      }
    `;

    try {
      const result = await this.makeQuery(query);
      
      const board = result.boards[0];
      if (!board) {
        throw new Error('Board not found');
      }

      const items = board.items_page?.items || [];

      return {
        id: board.id,
        name: board.name,
        description: board.description || '',
        columns: board.columns.map(col => ({
          id: col.id,
          title: col.title,
          type: col.type
        })),
        sampleItems: items.map((item, index) => ({
          id: item.id,
          name: item.name,
          createdAt: item.created_at,
          updatedAt: item.updated_at,
          index: index, // Add unique index for key generation
          values: item.column_values.reduce((acc, colValue) => {
            // Find the matching column definition to get the title
            const columnDef = board.columns.find(col => col.id === colValue.id);
            if (columnDef) {
              acc[columnDef.title] = {
                text: colValue.text,
                value: colValue.value,
                type: colValue.type
              };
            }
            return acc;
          }, {})
        })),
        totalColumns: board.columns.length,
        totalGroups: 0, // Not querying groups since your working implementation doesn't use them
        sampleItemCount: items.length
      };
    } catch (error) {
      console.error('Error fetching board preview:', error);
      throw new Error('Failed to fetch board preview');
    }
  }

  /**
   * Search for items in a board
   */
  async searchItems(boardId, searchTerm) {
    const query = `
      query ($boardId: [Int!]!) {
        boards(ids: $boardId) {
          items {
            id
            name
            column_values {
              id
              text
              value
            }
          }
        }
      }
    `;

    try {
      const result = await this.makeQuery(query, { boardId: [parseInt(boardId)] });
      const items = result.boards[0]?.items || [];

      if (searchTerm) {
        return items.filter(item => 
          item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          item.column_values.some(col => 
            col.text && col.text.toLowerCase().includes(searchTerm.toLowerCase())
          )
        );
      }

      return items;
    } catch (error) {
      console.error('Error searching items:', error);
      throw new Error('Failed to search items in Monday.com');
    }
  }

  /**
   * Get user information
   */
  async getUserInfo() {
    const query = `
      query {
        me {
          id
          name
          email
          photo_original
          is_admin
          account {
            id
            name
            plan {
              max_users
              period
              tier
            }
          }
        }
      }
    `;

    try {
      const result = await this.makeQuery(query);
      return result.me;
    } catch (error) {
      console.error('Error fetching user info:', error);
      throw new Error('Failed to fetch user information');
    }
  }

  /**
   * Helper method to format column values for Monday.com
   */
  static formatColumnValue(columnType, value) {
    switch (columnType) {
      case 'email':
        return { email: value, text: value };
      case 'phone':
        return { phone: value, countryShortName: 'US' };
      case 'date':
        return { date: value };
      case 'status':
        return { label: value };
      case 'rating':
        return { rating: parseInt(value) };
      case 'checkbox':
        return { checked: Boolean(value) };
      case 'numbers':
        return parseFloat(value);
      case 'text':
      case 'long_text':
        return value;
      default:
        return value;
    }
  }
}

export default MondayAPIService;
